import React, { useRef, useState } from "react";
import { api, API_BASE } from "../lib/api";
import { Button } from "./ui/button";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Resolve a stored URL to an absolute browser-loadable URL.
 * - "/api/files/<path>" → prefix with BACKEND
 * - Full "http(s)://..." URLs are returned as-is (back-compat for legacy image_url)
 */
export function resolveImageUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/")) return `${BACKEND}${url}`;
  if (url.startsWith("/")) return `${BACKEND}${url}`;
  return url;
}

/**
 * Direct-upload image picker used in admin forms.
 * Props:
 *   value        — current stored URL (returned by /api/uploads/image → "/api/files/<path>")
 *   onChange(url)— callback with the new stored URL after successful upload
 *   label        — display label above the picker
 *   testId       — data-testid prefix
 */
export default function ImageUploader({ value, onChange, label = "Image", testId = "image-upload" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const preview = value ? resolveImageUrl(value) : "";

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\//.test(f.type)) { toast.error("Please choose an image file"); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("Image must be under 8 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url); // "/api/files/<path>"
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clear = () => onChange("");

  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">{label}</div>
      <div className="rounded-lg border border-dashed border-[#334155] bg-[#0F172A] p-3 flex items-center gap-4">
        {preview ? (
          <div className="relative shrink-0">
            <img
              data-testid={`${testId}-thumbnail`}
              src={preview}
              alt="Uploaded"
              className="h-20 w-28 object-cover rounded-md border border-[#334155]"
              onError={(e) => { e.currentTarget.style.opacity = "0.4"; }}
            />
            <button
              type="button"
              onClick={clear}
              data-testid={`${testId}-clear`}
              aria-label="Remove image"
              className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-500 text-white grid place-items-center hover:bg-red-600 transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <div className="h-20 w-28 rounded-md border border-[#334155] bg-[#1E293B] grid place-items-center text-slate-500 text-[10px] uppercase tracking-[0.15em] shrink-0">
            No image
          </div>
        )}
        <div className="flex-1 min-w-0">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid={`${testId}-input`}
            onChange={onFile}
          />
          <Button
            type="button"
            variant="outline"
            onClick={pick}
            disabled={busy}
            data-testid={`${testId}-btn`}
            className="rounded-full border-[#334155] bg-transparent text-white hover:bg-[#1E293B] hover:text-white"
          >
            {busy ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading…</>) : (<><Upload className="mr-2 h-4 w-4" /> {preview ? "Replace image" : "Upload image"}</>)}
          </Button>
          <div className="mt-1 text-[10px] text-slate-500">JPEG, PNG, WEBP or GIF · up to 8 MB</div>
        </div>
      </div>
    </div>
  );
}
